import { ValidationRules } from "@/domain/records/value-objects/ValidationRules";
import { SetupConfig } from "@/domain/records/value-objects/SetupConfig";
import { GenerationRequest } from "@/domain/records/value-objects/GenerationRequest";
import type { ValidationResult } from "@/domain/records/types";
import { RESEARCH_CONTEXT_MAX_CHARS } from "@/domain/projects/constants/researchContextLimits";

/**
 * Domain Service for comprehensive validation
 * Clean Architecture - Domain Layer
 */
export class ValidationService {
  /**
   * Validate setup configuration with detailed feedback
   */
  static validateSetupConfig(config: SetupConfig): ValidationResult {
    return ValidationRules.validateSetupConfig(config);
  }

  /**
   * Validate generation request
   */
  static validateGenerationRequest(
    request: GenerationRequest
  ): ValidationResult {
    return ValidationRules.validateGenerationRequest({
      reportTypes: request.reportTypes,
      customPrompt: request.customPrompt,
      goals: request.goals,
      isWithTranscript: request.isWithTranscript,
      isWithInsightReport: request.isWithInsightReport,
    });
  }

  /**
   * Validate file for upload
   */
  static validateFileUpload(file: {
    size: number;
    type: string;
    name: string;
  }): ValidationResult {
    return ValidationRules.validateFileUpload(file);
  }

  /**
   * Validate version number
   */
  static validateVersion(version: number): ValidationResult {
    return ValidationRules.validateVersion(version);
  }

  /**
   * Validate record name
   */
  static validateRecordName(name: string): ValidationResult {
    const errors: string[] = [];

    if (!name || name.trim().length === 0) {
      errors.push("Record name is required");
    } else if (name.trim().length < 2) {
      errors.push("Record name must be at least 2 characters long");
    } else if (name.trim().length > 200) {
      errors.push("Record name must be less than 200 characters");
    }

    // Check for invalid characters
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(name)) {
      errors.push("Record name contains invalid characters");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate custom prompt requirements
   */
  static validateCustomPrompt(
    prompt: string,
    isCustomSelected: boolean
  ): ValidationResult {
    const errors: string[] = [];

    if (isCustomSelected) {
      if (!prompt || prompt.trim().length === 0) {
        errors.push(
          "Custom prompt is required when Custom report type is selected"
        );
      } else if (prompt.trim().length < 10) {
        errors.push("Custom prompt must be at least 10 characters long");
      } else if (prompt.trim().length > 5000) {
        errors.push("Custom prompt must be less than 5000 characters");
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate research context
   */
  static validateResearchContext(context: string): ValidationResult {
    const errors: string[] = [];

    if (!context || context.trim().length === 0) {
      errors.push("Research context is required");
    } else if (context.trim().length < 10) {
      errors.push("Research context must be at least 10 characters long");
    } else if (context.trim().length > RESEARCH_CONTEXT_MAX_CHARS) {
      errors.push(
        `Research context must be less than ${RESEARCH_CONTEXT_MAX_CHARS} characters`,
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate participant count
   */
  static validateParticipantCount(count?: number): ValidationResult {
    const errors: string[] = [];

    if (count !== undefined && count !== null) {
      if (!Number.isInteger(count)) {
        errors.push("Participant count must be a whole number");
      } else if (count < 1) {
        errors.push("Participant count must be at least 1");
      } else if (count > 15) {
        errors.push("Participant count cannot exceed 15");
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate respondent names
   */
  static validateRespondentNames(names: string[]): ValidationResult {
    const errors: string[] = [];

    if (names.length > 10) {
      errors.push("Maximum 10 respondents allowed");
    }

    names.forEach((name, index) => {
      if (name && name.length > 100) {
        errors.push(
          `Respondent ${index + 1} name must be less than 100 characters`
        );
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Comprehensive form validation with all field checks
   */
  static validateCompleteSetupForm(config: SetupConfig): ValidationResult {
    const errors: string[] = [];

    // Validate basic setup config
    const setupValidation = this.validateSetupConfig(config);
    errors.push(...setupValidation.errors);

    // Validate research context (goals)
    const contextValidation = this.validateResearchContext(config.goals);
    errors.push(...contextValidation.errors);

    // Validate participant count
    const participantValidation = this.validateParticipantCount(
      config.participantCount
    );
    errors.push(...participantValidation.errors);

    // Validate respondent names
    const respondentValidation = this.validateRespondentNames(
      config.respondentNames
    );
    errors.push(...respondentValidation.errors);

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}
